import React, { useState, useEffect, useRef } from "react";
import {useLocation} from 'react-router-dom';
import { Button, Divider, Drawer, Toolbar,Box, CircularProgress} from "@mui/material";
import StudyInformation from "./StudyInformation";
import BlendsAndBatches from "./BlendsAndBatches";
import StudySummary from "./StudySummary";
import BlendStudy from "../../../api/Formulations/BlendStudy";
import BlendRequest from "../../../api/Formulations/BlendRequest";
import ModalSimpleButton from "../../../components/Modal/ModalSimpleButton";
import ModalNoButton from "../../../components/Modal/ModalNoButton";
import { GlobalButton, GlobalSecondaryButton } from "../../../pages/styles";
import { RoundNumber, ConvertUOMs, GetRequiredSampleSize, GetRequiredSampleSizeUoM, isNumber, EvaluateRequiredIntegerField, EvaluateOptionalNumberField} from "../../../global";
import UnitOfMeasure from "../../../api/Admin/UnitOfMeasure";
import { useAuth } from "../../../context/AuthContext";
import { useUser } from "../../../context/UserContext";
import { Roles } from "../../../constants/roles";

const drawerWidth = 250
const navFontSize = '14px'

const headerTextStudy = 'Create Blend Study'
const instructionTextStudy = 'To create a new study, add the study information, start your blends and select your batches; then choose your test methods, if required by your study.'

const instructionTextBlends = 'To Prepare: Enter Blend information, select your components, add your batches and optionally add tests.'

const headerTextReview = 'Blend Study Details'
const instructionTextReview = ''

const errorsModalTitle = "Errors Detected"

const defaultBatch = {
  id: 0,
  blendID: 0,
  blendRequest: null,
  blendFacilityName: null,
  blendFacility: null,
  blendPriorityName: null,
  blendPriority: {blendPriorityName: 'Normal', isActive: true},
  priorityReason: null,
  batchComments: null,
  requestedCompletionDate: null,
  gpaqsNumber: null,
  actualAmount: null,
  preparedByEmail: null,
  preparedDate: null,
  lastModifiedByEmail: null,
  lastModifiedDate: null,
  createdByEmail: null,
  createdDate: null,
  destination: 'Internal',
  requestedAmount: null,
  requiresBlindCode: false,
  shippingInfo: null,
  preparedSampleName: null,
  requestedAmountUoM: null,
  unitOfMeasureRequested: null,
  actualAmountUoM: null,
  unitOfMeasureActual: null,
  batchStatusName: "Draft",
  batchStatus: null,
  batchSequenceNumber: 1,
  batchPrecision: 2,
  statusReason: null,
  batchRetainLocationName: null,
  batchRetainLocation: null,
  batchCertificationStatusName: null,
  batchCertificationStatus: null,
  lastCertifiedByEmail: null,
  lastCertifiedDate: null, 
  shelfLifeStartDate: null, 
  shelfLifeEndDate: null,
  waiverReason: null,
  retainReason: null,
  batchPurpose: null,
  shippingAddressID: null,
  batchAttachments: [],
  analyticalTesting: null,
  analyticalTestingObject: [],
  analyticalTestingOwnerEmail: null,
  batchProductGroupName: null, 
  batchProductGroup: null,
  isISOBlend: false,
  isMetalsFree: false,
  chargeCode: null,
  cmaOilCode: null,
  reference: null,
  safetyInformation: null,
  proMISRCode: null,
  description: null,
  batchAppearanceName: null, 
  freeWater: null,
  appearanceCommments: null

}

const defaultBlendComponent = {
  id: 0,
  blendID: 0,
  blendRequest: null,
  targetAmountUpper: null, //this is a place holder for when we implement upper and lower blending entries
  targetAmountLower: null,
  addOrder: null,
  isBalance: false,
  preferredSampleName: null,
  preferredSample: null,
  secondaryPreferredSampleName: null,
  secondaryPreferredSample: null,
  substanceName: null,
  substance: null,
  componentInstructions: null,
  blendAdditionTemperatureC: null,
  componentUoMName: null,
  componentUoM: {uoMName: 'wt%', type: 'weight', metricStandardUoMName: null, metricStandardConversion: null},
}

const defaultBlendRequest = {
  id: 0,
  blendStatusName: null,
  blendStatus: null,
  blendStudyID: 0,
  blendStudy: null,
  blendPurpose: null,
  keywords: null,
  createdByEmail: null,
  createdDate: null,
  lastModifiedByEmail: null,
  lastModifiedDate: null,
  psimsID: null,
  blendSubstanceName: null,
  blendName: 'A',
  blendComponents: [defaultBlendComponent],
  batches: [defaultBatch],
  blendComments: null,
  requestUoMName: null,
  requestUoM: {uoMName: 'wt%', type: 'weight', metricStandardUoMName: null, metricStandardConversion: null},
}

const defaultBlendStudy = 
 {
  id: 0,
  studyName: null,
  studyPurpose: null, 
  createdDate: null,
  createdByEmail: null,
  lastModifiedDate: null, 
  lastModifiedByEmail: null, 
  projectID: null, 
  project: null, 
  ownerEmail: null, 
  keywords: null, 
  blendStudyStatusName: null,
  blendStudyStatus: null,
  blendStudyTypeName: null, 
  blendStudyType: null, 
  ppmReference: null, 
  notes: null, 
  blendRequests: [defaultBlendRequest]
 };

 //set inital error value to Null if required field
 //set intial error value to false if optional field but has validation
const defaultBatchErrorCheck = 
{
  _blendLetter: 'A',
  _batchNumber: 1,
  requestedAmount: null,
  batchPrecision: false,
  unitOfMeasureRequested: null,
  batchRetainLocation: null,
  destination: false,
  blendPriority: false,
  priorityReason: false,
  blendFacility: null,
  chargeCode: null,
  batchProductGroup: null,
  description: null,
  analyticalTestingOwnerEmail: false
} 

const defaultBlendComponentErrorCheck =
{
  substance: null,
  targetAmountUpper: false, //place holder for when we implement upper and lower limits
  targetAmountLower: null,
  componentUoM: false,
  blendAdditionTemperatureC: false,
  preferredSample: false,
  secondaryPreferredSample: false,
}

const defaultBlendRequestErrorCheck =
{
  _blendLetter: 'A',
  requestUoM: false,
  blendComponents:[defaultBlendComponentErrorCheck],
  batches: [defaultBatchErrorCheck]
}

const defaultErrorChecksBlendStudy = 
{
  blendStudy:
  {
    studyName: null,
    project: false, 
    ownerEmail: null, 
    blendStudyType: null
  }, 
  blendRequests:[defaultBlendRequestErrorCheck],
};

const BlendStudyManagement = ({ ...props }) => {
  const { username } = useUser();
  const { hasAnyRole } = useAuth();
  const isBlendFormulator = hasAnyRole([Roles.BlendFormulator, Roles.Developer]);

  const location = useLocation()
  const [incomingStudyID, setIncomingStudyID] = useState(location.state && location.state.incomingStudyID ? location.state.incomingStudyID : -1)

  const [headerText, setHeaderText] = useState('')
  const [instructionText, setInstructionText] = useState('')

 //used to determine which screen in blend process to render
  const [displayScreenValue, setDisplayScreenValue] = useState(0);
  const [navBarTextColor, setNavBarTextColor] = useState(["#1460A6","#666666", "#666666"])
  
  const [myBlendStudy, setMyBlendStudy] = useState(defaultBlendStudy);

  const [errorChecks, setErrorChecks] = useState(defaultErrorChecksBlendStudy);

  const [isSaveChanges, setIsSaveChanges] = useState(false)

  const [modalMessagesOpen, setModalMessagesOpen] = useState(false);
  const modalMessagesButtonText = 'Ok'
  const [modalMessagesTitle, setModalMessagesTitle] = useState('');
  const [modalMessagesText, setModalMessagesText] = useState('');

  const [modalNoButtonOpen, setModalNoButtonOpen] = useState(false);
  const [modalNoButtonTitle, setModalNoButtonTitle] = useState('');
  const [modalNoButtonText, setModalNoButtonText] = useState('');

  const [blendPageHeader, setBlendPageHeader] = useState('Add Blends, Batches and Tests')

  const [availableUOMs, setAvailableUOMs] = useState([])
  const [errorLoadingBlends, setErrorLoadingBlends] = useState(null)

  const [reloadData, setReloadData] = useState(false)
  const [selectedBatchStatus, setSelectedBatchStatus] = useState(null)
  const inventoryCheckRef = useRef(null)
  
  function closeModalMessages() {
    setModalMessagesOpen(false);
  }

  function openModalMessages(title, text) {
    setModalMessagesOpen(true);
    setModalMessagesTitle(title);
    setModalMessagesText(text);
  }

  function closeModalNoButton() {
    setModalNoButtonOpen(false);
  }

  function openModalNoButton(title, text) {
    setModalNoButtonOpen(true);
    setModalNoButtonTitle(title);
    setModalNoButtonText(text);
  }

  async function handleNavagation(newValue) {

    let intNewValue = parseInt(newValue.currentTarget.value, 10)

    //study info page
    switch (intNewValue){
      case 0:{

        setDisplayScreenValue(intNewValue)

        return(
        setNavBarTextColor(["#1460A6","#666666", "#666666"])
        );
      }

      //blends and batches page
      case 1:{

      if (!(CheckStudyInfoForErrors()))
      {
        setDisplayScreenValue(intNewValue)

        return(
          setNavBarTextColor(["#1460A6","#1460A6", "#666666"])
          );
      } else {
        return null
      }
      
      }

      //Review Study
      case 2:{

        if (!(CheckStudyInfoForErrors()))
        {
          setDisplayScreenValue(intNewValue)

          return(
            setNavBarTextColor(["#1460A6","#1460A6", "#1460A6"])
          );

        } else {
          return null
        }
      }
      
      default: {
        alert(displayScreenValue);
      }
  }
  };
  
  function CheckStudyInfoForErrors(){
    let hasErrors = []

    if (errorChecks.blendStudy.studyName === true || errorChecks.blendStudy.studyName === null)
    {
        hasErrors.push("A blend study name must be entered.")
    }

    if (errorChecks.blendStudy.blendStudyType === true || errorChecks.blendStudy.blendStudyType === null)
    {
        hasErrors.push('Blend study type is required.') 

    }
    if (errorChecks.blendStudy.project === true || errorChecks.blendStudy.project === null)
    {
      hasErrors.push("A blend study project must be selected.")
    }

    if (errorChecks.blendStudy.ownerEmail === true || errorChecks.blendStudy.ownerEmail === null)
    {
      hasErrors.push("Blend study owner is required or is invalid.") 
    }

    if (hasErrors.length === 0) {
      return false
    } else {
      openModalMessages(errorsModalTitle, hasErrors, "Ok")
      return true
    }
  }

  function GetTotalNeededForTesting (blendRequestIndex, batchIndex, uomObject) {

    if (myBlendStudy.blendRequests[blendRequestIndex].batches[batchIndex].analyticalTestingObject === null || myBlendStudy.blendRequests[blendRequestIndex].batches[batchIndex].analyticalTestingObject.length < 1)
    {
      return 'N/A'

    } else {
      // for(var xBatch = 0; xBatch <  myBlendStudy.blendRequests[blendRequestIndex].batches.length; xBatch++)
      // {
        let totalNeededForTesting = 0

        myBlendStudy.blendRequests[blendRequestIndex].batches[batchIndex].analyticalTestingObject.forEach(oTest => {
          const testUoM = availableUOMs.find(obj => obj.uoMName === GetRequiredSampleSizeUoM(oTest))

          if (testUoM)
          {
            totalNeededForTesting += RoundNumber(ConvertUOMs(null, null, oTest.method.isPackageTest ? oTest.method.packageTestRequiredAmount :  GetRequiredSampleSize(oTest), testUoM, uomObject), 2)
          } else {
            return  `Test ${oTest.methodFacility.methodName}/${oTest.methodFacility.testFacilityAbv} appears to be missing a required sample size UoM`
          }          
        });
        
        return totalNeededForTesting;
      //}
    } 
  }

  function PreparePayloadToSaveChanges()
  {
    let copyStudy = structuredClone(myBlendStudy)

    //only included edit BlendRequests and Blend
    if (copyStudy.id !== 0)
    {
      //only get blend requests that are new, have components new/edited or have batches new/edited
      copyStudy.blendRequests = structuredClone(copyStudy.blendRequests.filter(request => request.id === 0 
                                    || request.hasBeenEdited === true 
                                    || request.blendComponents.filter(component => component.id === 0).length > 0
                                    || request.batches.filter(batch => batch.id === 0 || batch.hasBeenEdited === true).length > 0))

      for(var y = 0; y <  copyStudy.blendRequests.length; y++) 
      {
        copyStudy.blendRequests[y].batches = structuredClone(copyStudy.blendRequests[y].batches.filter(batch => batch.id === 0 || batch.hasBeenEdited === true))
      }
    }

    if (copyStudy.blendRequests.length === 0)
    {
      openModalMessages('Nothing Changed', "No changes found to save!", "Ok")
      return;
    }

    if (copyStudy.projectID === null)
    {
      copyStudy.projectID = 1
    }

    for(var i = 0; i <  copyStudy.blendRequests.length; i++) 
    {
      
      if (copyStudy.blendRequests[i].id === 0)
      {
        copyStudy.blendRequests[i].blendStudyID = 0
      }

      for(var x = 0; x <  copyStudy.blendRequests[i].blendComponents.length; x++) 
      {

        if (copyStudy.blendRequests[i].blendComponents[x].id === 0)
        {
          copyStudy.blendRequests[i].blendComponents[x].blendID = 0
        }

        //set the target upper = lower until ranges are implemented
        copyStudy.blendRequests[i].blendComponents[x].targetAmountUpper = copyStudy.blendRequests[i].blendComponents[x].targetAmountLower
      }

      for(var z = 0; z <  copyStudy.blendRequests[i].batches.length; z++) 
      {
        if (copyStudy.blendRequests[i].batches[z].id === 0)
        {
          copyStudy.blendRequests[i].batches[z].blendID = 0
        }

        copyStudy.blendRequests[i].batches[z].analyticalTesting = copyStudy.blendRequests[i].batches[z].analyticalTestingObject.length === 0 ? null : JSON.stringify(copyStudy.blendRequests[i].batches[z].analyticalTestingObject)
      }
    }

    //check the blend components for the ones being saved to ensure they are complete
    let errors = CheckBlendComponents(copyStudy)

    if (errors.length > 0) 
    {
      openModalMessages(errorsModalTitle, errors, "Ok")
      return;
    };

    const formData = new FormData();

    formData.append("studyJSON", JSON.stringify(copyStudy))

    copyStudy.blendRequests.forEach(oRequest => {
      // oRequest.blendAttachments.forEach(oAttachment => {
      //   if (oAttachment.id === null || oAttachment.id < 1) {
      //     formData.append("filesToUpload", oAttachment.fileObject, oAttachment.fileName)
      //   }
      // })

      oRequest.batches.forEach(oBatch => {
        oBatch.batchAttachments.forEach(batchAttachment => {
          if (batchAttachment.id === null || batchAttachment.id < 1) {
            formData.append("filesToUpload", batchAttachment.fileObject, batchAttachment.fileName)
          }
        })
      })
    })

    SubmitBlendStudy(formData, copyStudy.studyName, false);
  }

  function SubmitBlendStudy (payloadToSubmit, studyName, isSubmit) 
  {
    if (isSubmit === true)
    {
      openModalNoButton("Submitting Batch", "Please wait while the batch is submitted.")

      setIsSaveChanges(false)

    } else {
      setIsSaveChanges(true)
    }

    BlendStudy.CreateBlendStudy(payloadToSubmit).then((res) => {

      closeModalNoButton()

      if (res && res.message === 'Success') 
      {

        if (isSubmit === true)
        {
          setIncomingStudyID(res.result.id)
          openModalMessages('Batch Submitted', `Batch for ${studyName} submitted successfully!`)
          setReloadData(!reloadData)
        } else {
          setIncomingStudyID(res.result.id)
          openModalMessages('Blends Saved', `Blend Study ${studyName} information saved successfully!`)
          setReloadData(!reloadData)
        }

      } else {
        openModalMessages('Blend Study Failed to Save', ` ${res ? res.message : 'Unspecified Error'}. Contact support if you feel this is an error.`);          
      }

      if (isSubmit === false)
      {
        setIsSaveChanges(false)
      }
    });
  }

  async function GetBlends (perPage, page, studyID) {
    let cancelPromiseGetBlends = false
    let allData = []

    async function getData(perPage, page, allData) {
      let data = [];

      if (cancelPromiseGetBlends) return allData
  
      await BlendRequest.GetBlendsByStudyID(studyID, page, perPage).then((res) => {
        if (res.message === "Success")
        {
          data = res ? res.result : null
        } else {
          setErrorLoadingBlends(`There was an error getting the blend requests for study ${studyID}, ${res && res.message ? res.message :  ""}`)
        }
      });
    
      if (data !== null) {
        allData = [...allData, ...data]
      }
    
      if (data === null || data.length === 0) {
        return allData;
      } else {
        page++;
        setModalNoButtonText(`Please wait while blend data is loading, this may take longer with larger studies...Loaded ${allData.length} blends`)
        return getData(perPage, page, allData);
      }
    }

    var myRequests = await getData(perPage, page, allData)

    return myRequests;
  }

  useEffect(() => {
    if (displayScreenValue === 0)
    {
      setHeaderText(headerTextStudy)
      setInstructionText(instructionTextStudy)
    } else if (displayScreenValue === 1)
    {
      setHeaderText(blendPageHeader)
      setInstructionText(instructionTextBlends)
    } else if (displayScreenValue === 2)
    {
      setHeaderText(headerTextReview)
      setInstructionText(instructionTextReview)
      
    } else {
      setHeaderText(headerTextStudy)
      setInstructionText(instructionTextStudy)
    }
  }, [displayScreenValue, blendPageHeader])

  useEffect(() => {
    if (incomingStudyID > -1)
    {
      let cancelPromise = false
      
      //open loading modal
      openModalNoButton("Loading Blend Data", "Please wait while blend data is loading, this may take longer with larger studies...")

      BlendStudy.getBlendStudyByID(incomingStudyID, true).then((res) => {

        if (cancelPromise) return

        if (res.message === "Success")
        {      
          setBlendPageHeader(`Add Blends, Batches and Tests to '${res.result.studyName}'`)

          GetBlends(20, 1, res.result.id).then((data) => {

          if (errorLoadingBlends === null)
          {
            closeModalNoButton()
          }

          res.result.blendRequests = data;

          const copyStudy = structuredClone(res.result)


           const newErrorChecks = 
           {
             blendStudy:
             {
              studyName: copyStudy.studyName === null || copyStudy.studyName.trim() === '',
              project: copyStudy.project === null, 
              ownerEmail: copyStudy.ownerEmail === null || copyStudy.ownerEmail.trim() === '', 
              blendStudyType: copyStudy.blendStudyType === null
             }, 
             blendRequests:[],
           };

          for(var i = 0; i <  copyStudy.blendRequests.length; i++) {

            copyStudy.blendRequests[i].hasBeenEdited = false;

            newErrorChecks.blendRequests.push({
              requestUoM: copyStudy.blendRequests[i].requestUoM === null,
              blendComponents:[],
              batches: [],
              _blendLetter: copyStudy.blendRequests[i].blendName,
            })

            for(var x = 0; x <  copyStudy.blendRequests[i].blendComponents.length; x++) {
          
              newErrorChecks.blendRequests[i].blendComponents.push({
                _blendLetter: copyStudy.blendRequests[i].blendName,
                substance: copyStudy.blendRequests[i].blendComponents[x].substance === null,
                targetAmountUpper: EvaulateMaxUpperAmountPercentages(copyStudy, i, copyStudy.blendRequests[i].blendComponents[x].targetAmountUpper),
                targetAmountLower: EvaulateMaxUpperAmountPercentages(copyStudy, i, copyStudy.blendRequests[i].blendComponents[x].targetAmountLower),
                blendAdditionTemperatureC: EvaluateOptionalNumberField(copyStudy.blendRequests[i].blendComponents[x].blendAdditionTemperatureC),
                preferredSample: false,
                secondaryPreferredSample: false
              })
            }

            for(var z = 0; z <  copyStudy.blendRequests[i].batches.length; z++) {
              
              copyStudy.blendRequests[i].batches[z].hasBeenEdited = false;

              if (copyStudy.blendRequests[i].batches[z].destination === '' || copyStudy.blendRequests[i].batches[z].destination === null) {
                copyStudy.blendRequests[i].batches[z].destination = 'Internal'
              }

              copyStudy.blendRequests[i].batches[z].analyticalTestingObject = copyStudy.blendRequests[i].batches[z].analyticalTesting === null || copyStudy.blendRequests[i].batches[z].analyticalTesting === '' ? [] : JSON.parse(copyStudy.blendRequests[i].batches[z].analyticalTesting)
            
              newErrorChecks.blendRequests[i].batches.push({
                _blendLetter: copyStudy.blendRequests[i].blendName,
                _batchNumber: copyStudy.blendRequests[i].batches[z].batchSequenceNumber,
                requestedAmount: !(isNumber(copyStudy.blendRequests[i].batches[z].requestedAmount)),
                batchPrecision: EvaluateRequiredIntegerField(copyStudy.blendRequests[i].batches[z].batchPrecision),
                unitOfMeasureRequested: copyStudy.blendRequests[i].batches[z].unitOfMeasureRequested === null,
                batchRetainLocation: copyStudy.blendRequests[i].batches[z].batchRetainLocation === null,
                destination: copyStudy.blendRequests[i].batches[z].destination.trim() === '',
                blendPriority: copyStudy.blendRequests[i].batches[z].blendPriority === null,
                priorityReason: copyStudy.blendRequests[i].batches[z].priorityReason === '' && copyStudy.blendRequests[i].batches[z].blendPriority ? copyStudy.blendRequests[i].batches[z].blendPriority.blendPriorityName === 'Normal' ? false : true : false,
              
                blendFacility: copyStudy.blendRequests[i].batches[z].blendFacility === null,
                description: copyStudy.blendRequests[i].batches[z].description === '' || copyStudy.blendRequests[i].batches[z].description === null,
                chargeCode: copyStudy.blendRequests[i].batches[z].chargeCode === '' || copyStudy.blendRequests[i].batches[z].chargeCode === null,
                batchProductGroup: copyStudy.blendRequests[i].batches[z].batchProductGroup === null,
              })

            }

            //need this in case the incoming blend does not have any batches or components on the request
            if (copyStudy.blendRequests[i].batches.length === 0)
            {
              copyStudy.blendRequests[i].batches.push(defaultBatch)
              newErrorChecks.blendRequests[i].batches.push(defaultBatchErrorCheck)
            }

            if (copyStudy.blendRequests[i].blendComponents.length === 0)
            {
              copyStudy.blendRequests[i].blendComponents.push(defaultBlendComponent)
              newErrorChecks.blendRequests[i].blendComponents.push(defaultBlendComponentErrorCheck)

              //need this because when going from M2 to M3 there were closed blend requests without components
              if (copyStudy.blendRequests[i].blendStatusName === "Cancelled" || copyStudy.blendRequests[i].blendStatusName === "Complete")
              {
                newErrorChecks.blendRequests[i].blendComponents[0].substance = false
                newErrorChecks.blendRequests[i].blendComponents[0].targetAmountLower = false
                newErrorChecks.blendRequests[i].blendComponents[0].componentUoM = false
              }
            }
          }

          //need this in case an incoming study doesn't have any blends setup yet
          if (copyStudy.blendRequests.length === 0) 
          {
            copyStudy.blendRequests.push(defaultBlendRequest)
            newErrorChecks.blendRequests.push(defaultBlendRequestErrorCheck)
          }

          setMyBlendStudy(copyStudy)
          setErrorChecks(newErrorChecks)

        })
        } else {
          openModalMessages("Error Loading", `There was an error loading the Blend Study ${incomingStudyID}, please try again.  ${res.message ? res.message : ''} `)
        }
        });
      
      return () => {
        cancelPromise = true
    }
    
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingStudyID, reloadData])

  useEffect(() => {
    let cancelPromise = false

    UnitOfMeasure.getAll().then((res) => {
        if (cancelPromise) return
        setAvailableUOMs(res.filter(result => (result.metricStandardConversion !== null && result.isActive === true)))
    });
    
    return () => {
        cancelPromise = true
    }

  }, [])

 const renderCorrectPage = () => {
  switch (displayScreenValue) {
    case 0: {

      return (
        <StudyInformation
          myStudy={myBlendStudy}
          setMyStudy={setMyBlendStudy}
          errorChecks={errorChecks}
          setErrorChecks={setErrorChecks}
        ></StudyInformation>

      );
    }
    case 1: {

      return (
        <BlendsAndBatches
          myStudy={myBlendStudy}
          setMyStudy={setMyBlendStudy}
          errorCheck={errorChecks}
          setErrorCheck={setErrorChecks}
          defaultBlendRequest={defaultBlendRequest}
          defaultBlendComponent={defaultBlendComponent}
          defaultBlendComponentErrorCheck={defaultBlendComponentErrorCheck}
          defaultBatch={defaultBatch}
          defaultBatchErrorCheck={defaultBatchErrorCheck}
          defaultBlendRequestErrorCheck={defaultBlendRequestErrorCheck}
          GetTotalNeededForTesting={GetTotalNeededForTesting}
          CheckBlendComponents={CheckBlendComponents}
          SubmitBlendStudy={SubmitBlendStudy}
          SumOfBlendTargets={SumOfBlendTargets}
          openModalNoButton={openModalNoButton}
          closeModalNoButton={closeModalNoButton}
          reloadData={reloadData}
          setReloadData={setReloadData}
          inventoryCheckRef={inventoryCheckRef}
          setSelectedBatchStatus={setSelectedBatchStatus}
        ></BlendsAndBatches>
      );
    }
    case 2: {

      return (
        <StudySummary
          myStudy={myBlendStudy}
        ></StudySummary>
      );
    }

    default: {
      alert(displayScreenValue);
    }
  }
  };

  const renderNavagationButton = () => {
    switch (displayScreenValue) {

    case 0: {
        return (
            <GlobalButton
            name="btnBlendAndBatches"
            variant='contained'
            style={{ textTransform: "none",  marginTop:"50px", marginLeft:"15px", marginRight:"10px", width:"90%"}}
            value={1}
            onClick={handleNavagation}
            >Add Blends and Batches</GlobalButton>
        );
      }
      case 1: {
        return (
          isSaveChanges ?
          (<>
              <CircularProgress style={{textTransform: "none",  marginTop:"50px", marginLeft:"15px"}}></CircularProgress>
              <GlobalSecondaryButton
                disabled
                name="btnCheckInventory"
                variant='contained'
                style={{ textTransform: "none",  marginTop:"-30px", marginLeft:"10px", marginRight:"10px", width:"60%"}}
              >Check Inventory</GlobalSecondaryButton>

              <GlobalButton  
                name="btnRegister"
                disabled
                variant='contained'
                style={{ textTransform: "none",  marginTop:"1rem", marginLeft:"10px", marginRight:"10px", width:"60%"}}
              >Save All Changes</GlobalButton>

              <GlobalSecondaryButton
                disabled
                name="btnRegister"
                variant='contained'
                style={{ textTransform: "none",  marginTop:"1rem", marginLeft:"15px", marginRight:"10px", width:"90%"}}
                value={2}
              >Review Study</GlobalSecondaryButton>
            </>
          ) : (
            <>
              <GlobalSecondaryButton
                disabled={selectedBatchStatus !== 'Draft'}
                name="btnCheckInventory"
                variant='contained'
                style={{ textTransform: "none",  marginTop:"50px", marginLeft:"15px", marginRight:"10px", width:"90%"}}
                onClick={() => inventoryCheckRef.current && inventoryCheckRef.current()}
              >Check Inventory</GlobalSecondaryButton>

              <GlobalButton  
                disabled={!(isBlendFormulator) && (username !== myBlendStudy.ownerEmail)}
                name="btnRegister"
                variant='contained'
                style={{ textTransform: "none",  marginTop:"1rem", marginLeft:"15px", marginRight:"10px", width:"90%"}}
                onClick={(e) => PreparePayloadToSaveChanges()}
              >Save All Changes</GlobalButton>

              <GlobalSecondaryButton
                name="btnRegister"
                variant='contained'
                style={{ textTransform: "none",  marginTop:"1rem", marginLeft:"15px", marginRight:"10px", width:"90%"}}
                value={2}
                onClick={handleNavagation}
              >Review Study</GlobalSecondaryButton>
            </>
          )
        );
      }
  
      case 2: {
          return (
            isSaveChanges ?
            (<>
                <CircularProgress style={{textTransform: "none",  marginTop:"50px", marginLeft:"15px"}}></CircularProgress>
                <GlobalButton  
                  name="btnRegister"
                  disabled
                  variant='contained'
                  style={{ textTransform: "none",  marginTop:"-30px", marginLeft:"10px", marginRight:"10px", width:"60%"}}
                >Save All Changes</GlobalButton>
              </>
            ) : (
              <GlobalButton  
                disabled={!(isBlendFormulator) && (username !== myBlendStudy.ownerEmail)}
                name="btnRegister"
                variant='contained'
                style={{ textTransform: "none",  marginTop:"50px", marginLeft:"15px", marginRight:"10px", width:"90%"}}
                onClick={(e) => PreparePayloadToSaveChanges()}
              >Save All Changes</GlobalButton>
            )
            // isStudySaving ?
            // (<>
            //     <CircularProgress style={{textTransform: "none",  marginTop:"50px", marginLeft:"15px"}}></CircularProgress>
            //     <GlobalButton  
            //       name="btnRegister"
            //       disabled
            //       variant='contained'
            //       style={{ textTransform: "none",  marginTop:"-30px", marginLeft:"10px", marginRight:"10px", width:"60%"}}
            //     >Submit Study</GlobalButton>
            // </>
            // ):(
            //     <GlobalButton  
            //       disabled={!(isBlendFormulator) && (username !== myBlendStudy.ownerEmail)}
            //       name="btnRegister"
            //       variant='contained'
            //       style={{ textTransform: "none",  marginTop:"50px", marginLeft:"15px", marginRight:"10px", width:"90%"}}
            //       onClick={(e) => SubmitBlendStudy()}
            //     >Submit Study</GlobalButton>
            // )
          )
        }
    
      default: {
        alert(displayScreenValue);
      }
    }
  }

  function CheckBlendComponents (myStudy) 
  {
    let hasErrors = []

    for (var iBlend = 0; iBlend < myStudy.blendRequests.length; iBlend++)
    {
      let blendLetter = myStudy.blendRequests[iBlend].blendName
      let blendErrorObj = errorChecks.blendRequests.find(obj => obj._blendLetter === blendLetter)

      if (blendErrorObj.requestUoM === true || blendErrorObj.requestUoM === null)
      {
        hasErrors.push(`A blend request measurment type is required on blend ${blendLetter}.`)
      }
  
      for(var iComponent = 0; iComponent < blendErrorObj.blendComponents.length; iComponent++) 
      {    
        if (blendErrorObj.blendComponents[iComponent].substance === true || blendErrorObj.blendComponents[iComponent].substance === null)
        {
          hasErrors.push(`A ChemID is required for all components on blend ${blendLetter}.`)
        }

        if ((blendErrorObj.blendComponents[iComponent].targetAmountLower === true || blendErrorObj.blendComponents[iComponent].targetAmountLower === null))
        {
          hasErrors.push(`A lower amt. % is missing/invalid on blend ${blendLetter}.`)
        }

        if (blendErrorObj.blendComponents[iComponent].blendAdditionTemperatureC === true || blendErrorObj.blendComponents[iComponent].blendAdditionTemperatureC === null)
        {
          hasErrors.push(`A component addition temp is invalid on blend ${blendLetter}.`)
        }

        if (blendErrorObj.blendComponents[iComponent].preferredSample === true || blendErrorObj.blendComponents[iComponent].preferredSample === null)
        {
          hasErrors.push(`A component preferred MIDAS # is invalid on blend ${blendLetter}.`)
        }

        if (blendErrorObj.blendComponents[iComponent].secondaryPreferredSample === true || blendErrorObj.blendComponents[iComponent].secondaryPreferredSample === null)
        {
          hasErrors.push(`A component secondary preferred MIDAS # is invalid on blend ${blendLetter}.`)
        }

        if (blendErrorObj.blendComponents[iComponent].componentUoM === true || blendErrorObj.blendComponents[iComponent].componentUoM === null)
        {
          hasErrors.push(`A component is missing a unit of measure on blend ${blendLetter}.`)
        }
      }

      const target = SumOfBlendTargets(myStudy.blendRequests[iBlend].blendComponents)
      
      if (target < 100 && myStudy.blendRequests[iBlend].blendStatusName !== "Complete" && myStudy.blendRequests[iBlend].blendStatusName !== "Cancelled")
      {
        hasErrors.push(`The component target amounts total of ${target}% is less than 100% on blend ${blendLetter}.`)
      }      
    }

    return hasErrors;
  }

  function EvaulateMaxUpperAmountPercentages (myStudy, blendNumber, value)
  {
    if (value === null || value === '') 
    {
        return false
    } else
    {
      let numberCheck = isNumber(value)
      let totalPercentage = 0
      let requestUoM = myStudy.blendRequests[blendNumber].requestUoM;

      if (numberCheck)
      {
        myStudy.blendRequests[blendNumber].blendComponents.forEach((oComponent) => {     
          
          let componentUoM = oComponent.componentUoM

          if ((requestUoM !== null && componentUoM !== null) && requestUoM.uoMName === componentUoM.uoMName)
          {
            if (isNumber(oComponent.targetAmountUpper))
            {
              totalPercentage = RoundNumber(totalPercentage + RoundNumber(oComponent.targetAmountUpper, 3), 3)
            }
          }
        })

        return(totalPercentage > 100)
      }
      else
      {
        return(numberCheck)
      }
    }

  }

  function SumOfBlendTargets (lstBlendComponents) {

    let sum = 0;

    for(var iComponent = 0; iComponent <  lstBlendComponents.length; iComponent++) 
    {
        if (isNumber(lstBlendComponents[iComponent].targetAmountLower) && lstBlendComponents[iComponent].componentUoM 
        && (lstBlendComponents[iComponent].componentUoM.uoMName === 'wt%' || lstBlendComponents[iComponent].componentUoM.uoMName === 'vol%'))
        {
            sum = sum + Number(lstBlendComponents[iComponent].targetAmountLower)
        }
    }

    return RoundNumber(sum, 2);
  }

    return (
      <div>
        <span className='pageheader'>{headerText}</span>
        <Divider className='dividerbar' />
        
        <p style={{
            color:"#111112",
            fontSize:"15px",
            fontFamily:"EMprint",
        }}>{instructionText}</p>

      <Drawer sx={{ 
          width: 200,
          flexShrink: 0,
           [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
          marginTop:"25px",
         }} 
        variant="permanent"
        open
        anchor="right"
      >
        <Box style={{height:"69px"}}>test</Box>
        <Toolbar/>
        <Divider />
  
          <p style={{
               fontFamily:"EMprint",
               fontWeight:"600",
               fontSize:"20px",
               color:"#545459",
               marginBottom:"35px",
               textTransform: 'none',
               textAlign:"left",
               paddingLeft:"20px"
              }}>Study Summary</p>

          <Button style={{
                color:navBarTextColor[0],
                fontFamily:"EMprint",
                fontWeight:"600",
                fontSize: navFontSize,
                textTransform: 'none',
                alignSelf: "flex-start",
                marginTop:"-20px",
                paddingLeft:"20px"
              }}
              value={0}
              onClick={handleNavagation}
              >Study Information</Button>
              <p></p>

          <Button style={{
            color:navBarTextColor[1],
            fontFamily:"EMprint",
            fontWeight:"600",
            fontSize: navFontSize,
            textTransform: 'none',
            alignSelf: "flex-start",
            marginTop:"20px",
            paddingLeft:"20px"
          }}
            value={1}
            onClick={handleNavagation}
          >Add Blends and Batches</Button>

          <Button style={{
            color:navBarTextColor[2],
            fontFamily:"EMprint",
            fontWeight:"600",
            fontSize: navFontSize,
            alignSelf: "flex-start",
            textTransform: 'none',
            marginTop:"20px",
            paddingLeft:"20px"
          }}
            uppercase='false'
            value={2}
            onClick={handleNavagation}
          >Review Blend Study</Button>

        <div style={{bottom:0, position:"absolute", marginBottom:"2rem"}}>
           {renderNavagationButton()}
        </div>
     
  </Drawer>

        <Box style={{width:`calc(112% - ${drawerWidth}px)`}}>
          {renderCorrectPage()}
        </Box>

        {/* Informational Messages */}
        <ModalSimpleButton title={modalMessagesTitle} buttonText={modalMessagesButtonText} buttonAction={closeModalMessages} open={modalMessagesOpen} setOpen={setModalMessagesOpen}>
            {modalMessagesTitle === errorsModalTitle ?
            (modalMessagesText && modalMessagesText.map((text, index) => {
                return (
                    <div style={{display:"flex"}} key={`myBlendingErrorChecks${index}`}>
                        <label>
                        {text}
                        </label>
                    </div>
                )
                })
            )
            :
            ( <label>
                {modalMessagesText}
              </label>
            )}      
        </ModalSimpleButton>

        {/* Informational Messages */}
        <ModalNoButton title={modalNoButtonTitle} open={modalNoButtonOpen} setOpen={setModalNoButtonOpen}>
                <label>
                  {modalNoButtonText}
                </label>
        </ModalNoButton>
  </div> 
    
      );
    };
    
export default BlendStudyManagement;